import KogModalSideComponent from '@/main/components/modal/ModalSide';
import KOGAutoCompleteCheckbox from '@/main/components/shared/inputAutoCompleteCheck';
import { KeyValue } from '@/main/models/common';
import { getTranslateByKey, removeEmptyValues } from '@/main/services/utils';
import { getCategory, useCategoryReducer } from '@/main/store/category';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import * as S from './styles';
import {
  getSubcategory,
  useSubcategoryReducer
} from '@/main/store/subcategory';
import Button from '@/main/components/shared/button/button';
import {
  SaveFavoriteCategory,
  XnuupPartIdAutocompleteFilterRequest
} from '@/main/models/request';
import { favoriteCategoryApi, partApi } from '@/main/api';
import KogPaginatedMultiSelect from '@/main/components/shared/kogPaginatedMultiselect/kogPaginatedMultiselect';
import { XnuupPartIdAutoCompleteResponse } from '@/main/models/response';
import { PaginationResponse } from '@/main/models/common/paginationResponse';
import { isNullUndefined } from '@/main/services/reducerUtils';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { toast } from 'react-toastify';
interface FavoriteCategoryForm {
  category: KeyValue[];
  subCategory: KeyValue[];
  parts: XnuupPartIdAutoCompleteResponse[];
}

const INITIAL_FORM_STATE: FavoriteCategoryForm = {
  category: [],
  subCategory: [],
  parts: []
};

const uniqueByKey = (items: KeyValue[]) => {
  const seen = new Set<string | number>();
  return items.filter(item => {
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
};

const sameKeys = (a: KeyValue[], b: KeyValue[]) =>
  a.length === b.length && a.every((item, i) => item.key === b[i].key);

interface IModalFavoriteCategory {
  open: boolean;
  onClose: (refresh: boolean) => void;
}

function ModalFavoriteCategoryContent({
  onClose
}: Readonly<IModalFavoriteCategory>) {
  const { category: categoryReducer } = useCategoryReducer();
  const { subcategory: subCategoryReducer } = useSubcategoryReducer();
  const dispatch = useDispatch();

  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [reloadTrigger, setReloadTrigger] = useState<boolean>(false);

  const { setValue, watch, handleSubmit, reset } = useForm<
    FavoriteCategoryForm
  >({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  const dataCategory = watch('category');
  const dataSubCategory = watch('subCategory');
  const dataParts = watch('parts');

  useEffect(() => {
    dispatch(getCategory(false));
    loadDetails();
  }, []);

  function loadSubCategories(categories: KeyValue[]) {
    if (categories.length === 0) return;
    dispatch(
      getSubcategory({
        isCanastaBasica: false,
        categoryGuids: categories.map(category => String(category.key))
      })
    );
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
  }

  function handleSave(data: FavoriteCategoryForm) {
    const request: SaveFavoriteCategory = {
      guidsPartCategory: data.category.map(category => String(category.key)),
      guidsPartSubCategory: data.subCategory.map(subCategory =>
        String(subCategory.key)
      ),
      guidsPart: data.parts.map(part => String(part.partGuid))
    };

    setIsLoadingSave(true);

    favoriteCategoryApi
      .save(request)
      .then(res => {
        const { result } = res.data;
        if (result) {
          onClose(true);
          toast.success(getTranslateByKey("information.favoriteCategorySavedSuccessfully"))
        }
      })
      .finally(() => {
        setIsLoadingSave(false);
      });
  }

  function loadDetails() {
    setIsLoadingDetails(true);
    favoriteCategoryApi
      .getDetails()
      .then(res => {
        const { result } = res.data;

        const categories = result.guidsPartCategory.map(item => ({
          key: item.guid,
          value: item.name
        }));
        setValue('category', categories);

        setValue(
          'subCategory',
          result.guidsPartSubCategory.map(item => ({
            key: item.guid,
            value: item.name
          }))
        );

        setValue(
          'parts',
          result.guidsPart.map(item => ({
            partGuid: item.guid,
            partPIESName: item.name
          }))
        );

        loadSubCategories(categories);
      })
      .finally(() => {
        setIsLoadingDetails(false);
        setReloadTrigger(prev => !prev);
      });
  }

  const loadParts = useCallback(
    async (
      searchTerm: string,
      pageIndex: number,
      pageSize: number
    ): Promise<PaginationResponse<XnuupPartIdAutoCompleteResponse>> => {
      if (isLoadingDetails) {
        return {
          results: [],
          totalNumberOfRecords: 0,
          pageNumber: 0,
          totalNumberOfPages: 0,
          isHideToastNotification: true,
          pageSize: 0
        };
      }
      const request: XnuupPartIdAutocompleteFilterRequest = removeEmptyValues({
        guidsPartCategory: dataCategory.map(category => String(category.key)),
        guidsPartSubCategory: dataSubCategory.map(subCategory =>
          String(subCategory.key)
        ),
        searchTerm: searchTerm,
        pageIndex: pageIndex,
        pageSize: pageSize,
        showIdPIES: false,
        useListFilterLogic: true
      });
      const res = await partApi.getXnuupPartId(request);
      return res.data;
    },
    [dataCategory, dataSubCategory, isLoadingDetails]
  );

  const handleSelect = (parts: XnuupPartIdAutoCompleteResponse[]) => {
    setValue('parts', parts);

    const categoriesSelected = parts
      .filter(
        part =>
          !isNullUndefined(part.partCategoryGuid) &&
          !isNullUndefined(part.partCategoryName)
      )
      .map(part => ({
        key: part.partCategoryGuid!,
        value: part.partCategoryName!
      }));

    const subCategoriesSelected = parts
      .filter(
        part =>
          !isNullUndefined(part.partSubCategoryGuid) &&
          !isNullUndefined(part.partSubCategoryName)
      )
      .map(part => ({
        key: part.partSubCategoryGuid!,
        value: part.partSubCategoryName!
      }));

    const newCategories = uniqueByKey([...dataCategory, ...categoriesSelected]);
    const newSubCategories = uniqueByKey([
      ...dataSubCategory,
      ...subCategoriesSelected
    ]);

    if (!sameKeys(dataCategory, newCategories)) {
      setValue('category', newCategories);
      loadSubCategories(newCategories);
    }
    if (!sameKeys(dataSubCategory, newSubCategories)) {
      setValue('subCategory', newSubCategories);
    }
  };

  const handleCategoryChange = (values: KeyValue[]) => {
    const categoryKeys = new Set(values.map(v => String(v.key)));

    const filteredParts = dataParts.filter(
      part => !part.partCategoryGuid || categoryKeys.has(part.partCategoryGuid)
    );

    setValue('category', values);
    loadSubCategories(values);
    setValue('subCategory', []);
    setValue('parts', filteredParts);
    setReloadTrigger(prev => !prev);
  };

  const handleSubCategoryChange = (values: KeyValue[]) => {
    const subCategoryKeys = new Set(values.map(v => String(v.key)));
    const categoryKeys = new Set(dataCategory.map(v => String(v.key)));

    const filteredParts = dataParts.filter(part => {
      if (!part.partSubCategoryGuid) return true;
      if (subCategoryKeys.has(part.partSubCategoryGuid)) return true;
      if (part.partCategoryGuid && categoryKeys.has(part.partCategoryGuid))
        return true;
      return false;
    });

    setValue('subCategory', values);
    setValue('parts', filteredParts);
    setReloadTrigger(prev => !prev);
  };

  return (
    <form noValidate onSubmit={handleSubmit(handleSave)}>
      <S.Container>
        <KOGAutoCompleteCheckbox
          label={getTranslateByKey('information.category')}
          isLoading={categoryReducer.isLoading || isLoadingDetails}
          options={categoryReducer.data || []}
          valueCustom={dataCategory}
          selectAllLabel={getTranslateByKey('information.all')}
          setValueCustom={handleCategoryChange}
          customInputProps={{
            required: true,
            id: 'id-favoriteCategory-select-category'
          }}
        />

        <KOGAutoCompleteCheckbox
          label={getTranslateByKey('quoteCard.subCategory')}
          isLoading={subCategoryReducer.isLoading || isLoadingDetails}
          options={subCategoryReducer.data || []}
          valueCustom={dataSubCategory}
          selectAllLabel={getTranslateByKey('information.all')}
          setValueCustom={handleSubCategoryChange}
          customInputProps={{
            required: true,
            id: 'id-favoriteCategory-select-subCategory'
          }}
        />

        <KogPaginatedMultiSelect<XnuupPartIdAutoCompleteResponse>
          loadData={loadParts}
          getOptionKey={opt => opt.partGuid ?? opt.partPIESName}
          getOptionLabel={opt => opt.partPIESName}
          onSelect={handleSelect}
          value={dataParts}
          label={getTranslateByKey('information.part')}
          disabled={isLoadingDetails}
          reloadTrigger={reloadTrigger}
        />

        <S.AreaButtons>
          <SecondaryButton onClick={handleClear}>
            {getTranslateByKey('information.clear')}
          </SecondaryButton>
          <Button type="submit" loading={isLoadingSave}>
            {getTranslateByKey('information.save')}
          </Button>
        </S.AreaButtons>
      </S.Container>
    </form>
  );
}

function ModalFavoriteCategory(props: Readonly<IModalFavoriteCategory>) {
  return (
    <KogModalSideComponent
      titleModalReducer="modalFavoriteCategory"
      title={getTranslateByKey('information.myFavoriteCategory')}
      open={props.open}
      closeModal={() => {
        props.onClose(false);
      }}>
      {props.open && <ModalFavoriteCategoryContent {...props} />}
    </KogModalSideComponent>
  );
}

export default ModalFavoriteCategory;
